import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import HelpTip from '@/components/HelpTip';
import { Loader2, Workflow, ArrowLeft, Save, Sparkles, ExternalLink } from 'lucide-react';

// Ayuda llana por destino (anti-pendejos): se muestra en un tooltip.
const DESTINO_AYUDA = 'Elige a dónde va a parar cada respuesta del formulario:\n' +
  '• Un dato del lead: lo guarda en su ficha (nombre, correo, origen o mensaje).\n' +
  '• Un dato personalizado: tú le pones nombre (ej. "zona" o "presupuesto") y lo guarda ahí.\n' +
  '• Guardar como nota: queda en el historial del lead, como texto.\n' +
  '• Mover de etapa (solo respuestas de opción): según lo que elija, mueve su oportunidad de etapa en el embudo.';

// Extrae los nombres de campo que devuelve un flow (keys del payload de la acción "complete"),
// para mostrar un preview llano de lo que la IA armó. Excluye flow_token (reservado).
function _flowFields(jsonStr) {
  const keys = [];
  const walk = (n) => {
    if (Array.isArray(n)) { n.forEach(walk); return; }
    if (n && typeof n === 'object') {
      if (n.name === 'complete' && n.payload && typeof n.payload === 'object') {
        Object.keys(n.payload).forEach((k) => { if (k !== 'flow_token' && !keys.includes(k)) keys.push(k); });
      }
      Object.values(n).forEach(walk);
    }
  };
  try { walk(JSON.parse(jsonStr || '{}')); } catch { /* noop */ }
  return keys;
}

// Etiquetas llanas (anti-pendejos): el usuario NO ve nombres tecnicos.
const STD_LABELS = {
  name: 'Nombre del lead',
  email: 'Correo',
  source: 'Origen',
  message: 'Mensaje / interes (texto)',
};

/**
 * Pestaña "Flows" del modulo WhatsApp del tenant.
 * Lista los formularios (Flows) del tenant y, por flow, deja elegir DONDE CAE en el lead
 * cada respuesta: dato del lead / dato personalizado / nota (F1), o mover de etapa (F2).
 * Backend: GET/POST /whatsapp/flows/{flow_id}/response-map ; lista: /whatsapp/flows/mine.
 */
export function FlowsTab() {
  const [flows, setFlows] = useState(null); // null = cargando
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadFlows = useCallback(() => {
    api.get('/whatsapp/flows/mine')
      .then((r) => { setFlows(r.data?.flows || []); setNote(r.data?.note || ''); })
      .catch(() => setFlows([]));
  }, []);

  useEffect(() => { loadFlows(); }, [loadFlows]);

  if (creating) return <FlowCreator onBack={(reload) => { setCreating(false); if (reload) { setFlows(null); loadFlows(); } }} />;
  if (selected) return <FlowMapEditor flow={selected} onBack={() => setSelected(null)} />;

  if (flows === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando tus flows…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="flows-tab">
      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
        <p className="text-sm text-white font-medium flex items-center gap-2">
          <Workflow className="w-4 h-4 text-emerald-400" /> Tus formularios (Flows)
          <HelpTip title="¿Qué es un Flow?" content="Un Flow es un formulario dentro de WhatsApp (nombre, teléfono, opciones…). El cliente lo llena sin salir del chat. Aquí eliges dónde cae en el lead cada respuesta que contesta." />
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Un Flow es un formulario dentro de WhatsApp. Aquí eliges <b>dónde cae en el lead</b> cada
          respuesta que el cliente contesta.
        </p>
        <Button size="sm" variant="secondary" className="mt-2.5 gap-1.5" onClick={() => setCreating(true)} data-testid="c3-create-flow">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Crear un formulario con IA
        </Button>
      </div>

      {flows.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-muted-foreground" data-testid="flows-empty">
          {note || 'Aún no tienes formularios (Flows) publicados en tu número. Cuando publiques uno, aparecerá aquí para configurar sus respuestas.'}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {flows.map((f) => (
            <div key={f.flow_id} className="rounded-xl border border-white/10 bg-black/20 p-3 flex flex-col gap-2" data-testid="flow-card">
              <div className="min-w-0">
                <p className="text-sm text-white font-medium truncate">{f.name || 'Flow sin nombre'}</p>
                <p className="text-[10px] text-muted-foreground truncate">ID {f.flow_id}</p>
              </div>
              <Button size="sm" variant="secondary" className="self-start" onClick={() => setSelected(f)}
                data-testid="flow-config-btn">
                ¿Dónde caen las respuestas?
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FlowMapEditor({ flow, onBack }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/whatsapp/flows/${encodeURIComponent(flow.flow_id)}/response-map`, { params: { app_id: flow.flow_app_id } })
      .then((r) => {
        const d = r.data || {};
        setData(d);
        const init = {};
        for (const sf of (d.source_fields || [])) init[sf] = { destino: 'note' };
        for (const e of (d.response_map || [])) {
          if (!e?.flow_field) continue;
          if (e.destino === 'standard_field') init[e.flow_field] = { destino: 'standard_field', field: e.params?.field || '' };
          else if (e.destino === 'custom_field') init[e.flow_field] = { destino: 'custom_field', key: e.params?.key || '' };
          else if (e.destino === 'stage') init[e.flow_field] = { destino: 'stage', value_map: { ...(e.params?.value_map || {}) } };
          else init[e.flow_field] = { destino: 'note' };
        }
        setRows(init);
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || 'No se pudo cargar el formulario'); setData({ source_fields: [], standard_fields: [] }); });
  }, [flow]);

  const setRow = (field, patch) => setRows((r) => ({ ...r, [field]: { ...(r[field] || {}), ...patch } }));
  const setVm = (field, optId, stageId) => setRows((r) => {
    const cur = r[field] || { destino: 'stage' };
    const vm = { ...(cur.value_map || {}) };
    if (stageId) vm[optId] = stageId; else delete vm[optId];
    return { ...r, [field]: { ...cur, value_map: vm } };
  });

  const save = async () => {
    setSaving(true);
    try {
      const response_map = [];
      for (const [flow_field, v] of Object.entries(rows)) {
        if (v.destino === 'standard_field' && v.field) {
          response_map.push({ flow_field, destino: 'standard_field', params: { field: v.field } });
        } else if (v.destino === 'custom_field' && (v.key || '').trim()) {
          response_map.push({ flow_field, destino: 'custom_field', params: { key: v.key.trim() } });
        } else if (v.destino === 'stage') {
          const vm = {};
          for (const [optId, stId] of Object.entries(v.value_map || {})) { if (stId) vm[optId] = stId; }
          if (Object.keys(vm).length) response_map.push({ flow_field, destino: 'stage', params: { value_map: vm } });
        }
        // 'note' / sin elegir: no se guarda; el motor lo deja como nota por defecto.
      }
      await api.post(`/whatsapp/flows/${encodeURIComponent(flow.flow_id)}/response-map`, { app_id: flow.flow_app_id, response_map });
      toast.success('Guardado: ya sabemos dónde cae cada respuesta.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (data === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
      </div>
    );
  }

  const stdFields = data.standard_fields || ['name', 'email', 'source', 'message'];
  const stages = data.stages || [];
  const fieldOptions = data.field_options || {};
  const src = data.source_fields || [];
  const hasOpts = (field) => Array.isArray(fieldOptions[field]) && fieldOptions[field].length > 0;

  return (
    <div className="space-y-4" data-testid="flow-map-editor">
      <button type="button" onClick={onBack} className="text-xs text-emerald-300 flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver a mis flows
      </button>
      <div>
        <p className="text-sm text-white font-medium truncate">{flow.name || 'Flow'}</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          Cuando el cliente llene este formulario, ¿dónde quieres que caiga cada respuesta en el lead?
          <HelpTip title="Dónde cae cada respuesta" content={DESTINO_AYUDA} />
        </p>
      </div>

      {src.length === 0 ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          No pudimos leer los campos de este formulario ahora. Vuelve a intentar en un momento.
        </div>
      ) : (
        <div className="space-y-2">
          {src.map((field) => {
            const v = rows[field] || { destino: 'note' };
            return (
              <div key={field} className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-2">
                <p className="text-xs text-zinc-300">
                  Respuesta: <span className="text-white font-medium">{field}</span>
                  {hasOpts(field) && <span className="text-[10px] text-muted-foreground"> · opción múltiple</span>}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <select
                    value={v.destino}
                    onChange={(e) => setRow(field, { destino: e.target.value, field: '', key: '', value_map: {} })}
                    className="w-full sm:w-56 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                    data-testid={`dest-${field}`}
                  >
                    <option value="note">Guardar como nota</option>
                    <option value="standard_field">Un dato del lead</option>
                    <option value="custom_field">Un dato personalizado</option>
                    {hasOpts(field) && <option value="stage">Mover de etapa (embudo)</option>}
                  </select>
                  {v.destino === 'standard_field' && (
                    <select
                      value={v.field || ''}
                      onChange={(e) => setRow(field, { field: e.target.value })}
                      className="w-full sm:flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                    >
                      <option value="">Elige el dato…</option>
                      {stdFields.map((sf) => <option key={sf} value={sf}>{STD_LABELS[sf] || sf}</option>)}
                    </select>
                  )}
                  {v.destino === 'custom_field' && (
                    <input
                      type="text"
                      value={v.key || ''}
                      onChange={(e) => setRow(field, { key: e.target.value })}
                      placeholder="nombre del dato (ej. telefono_alterno)"
                      className="w-full sm:flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                    />
                  )}
                </div>

                {v.destino === 'stage' && (
                  <div className="space-y-1.5 pt-1 border-t border-white/5">
                    <p className="text-[10px] text-muted-foreground">Según la respuesta, mueve la oportunidad del lead a:</p>
                    {(fieldOptions[field] || []).map((opt) => (
                      <div key={opt.id} className="flex flex-col gap-1 sm:flex-row sm:items-center">
                        <span className="text-[11px] text-zinc-400 sm:w-40 truncate">Si responde “{opt.title}”</span>
                        <select
                          value={(v.value_map || {})[opt.id] || ''}
                          onChange={(e) => setVm(field, opt.id, e.target.value)}
                          className="w-full sm:flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
                        >
                          <option value="">— no mover —</option>
                          {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[10px] text-muted-foreground">Todo lo que llene el cliente queda además en el historial del lead.</p>
        <Button size="sm" onClick={save} disabled={saving || src.length === 0} className="gap-1.5 self-start" data-testid="flow-map-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </Button>
      </div>
    </div>
  );
}

// C3: el tenant crea un formulario (Flow) con IA sobre su propia app. describe -> genera -> crea -> publica.
function FlowCreator({ onBack }) {
  const [step, setStep] = useState('describe'); // describe | draft | created
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState('');
  const [created, setCreated] = useState(null);

  const generate = async () => {
    if (!brief.trim()) { toast.error('Escribe qué quieres que pida el formulario'); return; }
    setBusy(true);
    try {
      const r = await api.post('/whatsapp/flows/ai', { brief: brief.trim() });
      setDraft(r.data);
      setName(r.data?.name || 'Formulario');
      setStep('draft');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'La IA no pudo generar el formulario');
    } finally { setBusy(false); }
  };

  const create = async () => {
    if (!name.trim()) { toast.error('Ponle un nombre al formulario'); return; }
    setBusy(true);
    try {
      const r = await api.post('/whatsapp/flows/create', { name: name.trim(), flow_json: draft.flow_json });
      setCreated(r.data);
      setStep('created');
      toast.success('Formulario creado como borrador. Revísalo y publícalo.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear el formulario');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/whatsapp/flows/${encodeURIComponent(created.flow_id)}/publish`);
      toast.success('¡Formulario publicado! Ya lo puedes usar.');
      onBack(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo publicar');
    } finally { setBusy(false); }
  };

  const fields = _flowFields(draft?.flow_json);

  return (
    <div className="space-y-4" data-testid="flow-creator">
      <button type="button" onClick={() => onBack(false)} className="text-xs text-emerald-300 flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver a mis flows
      </button>
      <p className="text-sm text-white font-medium flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-emerald-400" /> Crear un formulario con IA
        <HelpTip title="Cómo funciona" content="1) Describe en tus palabras qué quieres preguntar. 2) La IA arma el formulario. 3) Lo previsualizas. 4) Lo publicas en tu número de WhatsApp. Después, en la lista, defines dónde cae cada respuesta en el lead." />
      </p>

      {step === 'describe' && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Describe qué quieres que pida el formulario, en lenguaje normal.</p>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={4}
            placeholder="Ej: Pide nombre, teléfono y qué le interesa (Casa, Departamento o Terreno)."
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
            data-testid="flow-brief"
          />
          <Button size="sm" onClick={generate} disabled={busy || !brief.trim()} className="gap-1.5" data-testid="flow-generate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar con IA
          </Button>
        </div>
      )}

      {step === 'draft' && draft && (
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[11px] text-zinc-400">Nombre del formulario</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" />
          </div>
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <p className="text-[11px] text-zinc-400 mb-1.5">La IA armó un formulario con estos campos:</p>
            {fields.length ? (
              <div className="flex flex-wrap gap-1.5">
                {fields.map((f) => (
                  <span key={f} className="text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white">{f}</span>
                ))}
              </div>
            ) : <p className="text-[10px] text-muted-foreground">Formulario generado.</p>}
            {(draft.warnings || []).length > 0 && (
              <p className="text-[10px] text-amber-300 mt-2">Avisos: {(draft.warnings || []).join('; ')}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setStep('describe')} disabled={busy}>Volver a describir</Button>
            <Button size="sm" onClick={create} disabled={busy} className="gap-1.5" data-testid="flow-create-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Crear formulario
            </Button>
          </div>
        </div>
      )}

      {step === 'created' && created && (
        <div className="space-y-3">
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-3">
            <p className="text-xs text-emerald-200 font-medium">Formulario creado como borrador ✓</p>
            <p className="text-[10px] text-emerald-200/80 mt-1">Revisa cómo se ve antes de publicarlo:</p>
            {created.preview_url ? (
              <a href={created.preview_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-emerald-300 underline mt-1">
                Ver vista previa <ExternalLink className="w-3 h-3" />
              </a>
            ) : <p className="text-[10px] text-muted-foreground mt-1">(sin vista previa)</p>}
          </div>
          <p className="text-[10px] text-muted-foreground">Al publicar, el formulario queda listo para usarse. Después, en la lista, define dónde cae cada respuesta en el lead.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => onBack(true)} disabled={busy}>Dejar como borrador</Button>
            <Button size="sm" onClick={publish} disabled={busy} className="gap-1.5" data-testid="flow-publish-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Publicar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlowsTab;
